import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { TelXLDropdownComponent, TelXLLoadingComponent } from '@dxp/elements';

import {
    CustomFieldDefinitionType,
    ICustomerDetails,
    ICustomFieldDefinition,
    IDropdown,
} from '@dxp/core/models';
import { CustomerDirectoryService, LoggerService } from '@dxp/core/services';
import { SimpleEmailValidator, UKNumberValidator } from '@dxp/core/validators';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import {
    catchError,
    combineLatest,
    map,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JsonPatchData } from '@dxp/core/types';
import { ValidateContactFieldDirective } from '@dxp/core/directives';
import {
    CustomerDetailsService,
    FormAction,
} from '../customer-details/customer-details.service';
import { EndCustomerApiService } from '../services/end-customer.api.service';
import { EndCustomerStateService } from '../services/end-customer.state.service';

const updatableCustomerProperties: (keyof ICustomerDetails)[] = [
    'firstName',
    'middleName',
    'lastName',
    'email',
    'phoneNumbers',
    'customFields',
    'preferredAgentId',
];

@Component({
    selector: 'end-customer-details-form',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TelXLDropdownComponent,
        TelXLLoadingComponent,
        NgbTooltip,
        ValidateContactFieldDirective,
    ],
    templateUrl: './customer-details-form.component.html',
    styleUrl: './customer-details-form.component.scss',
})
export class EndCustomerDetailsFormComponent implements OnInit {
    readonly #destroyRef = inject(DestroyRef);
    readonly #fb = inject(FormBuilder);
    readonly #endCustomerApiService = inject(EndCustomerApiService);
    readonly #endCustomerStateService = inject(EndCustomerStateService);
    readonly #customerDetailsService = inject(CustomerDetailsService);
    readonly #customerDirectoryService = inject(CustomerDirectoryService);
    readonly #logger = inject(LoggerService);

    updated = output<ICustomerDetails>();
    isCreateCustomer = input<boolean>(false);

    readonly #emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
    readonly #isEditing = signal<boolean>(false);
    readonly #internalCustomer = signal<ICustomerDetails | undefined>(
        undefined,
    );

    readonly customFieldDefinitions = signal<
        ICustomFieldDefinition[] | undefined
    >(undefined);
    readonly editing = computed<boolean>(() => this.#isEditing());

    customerForm!: FormGroup;
    #initialFormValue!: ICustomerDetails;

    ngOnInit() {
        this.#customerDetailsService.isEditing$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(isEditing => {
                this.#isEditing.set(isEditing);
                this.#initForm();
            });

        this.#customerDetailsService.formAction$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((action: FormAction) => {
                if (action === 'save') {
                    this.submitForm();
                } else if (action === 'cancel') {
                    this.#customerDetailsService.updateEditing(false);
                }
            });

        this.#customerDetailsService.selectedEndCustomer$.subscribe(
            customer => {
                this.#endCustomerApiService
                    .fetchCustomFields(
                        customer?.businessUnitId ??
                            this.#endCustomerStateService.bussinesUnit(),
                    )
                    .pipe(
                        tap(customerFieldDefinitions => {
                            this.customFieldDefinitions.set(
                                customerFieldDefinitions,
                            );

                            this.#internalCustomer.set(customer);
                            this.#initForm();
                        }),
                        takeUntilDestroyed(this.#destroyRef),
                    )
                    .subscribe();
            },
        );
    }

    #initForm() {
        this.customerForm = this.#fb.group(
            {
                firstName: [
                    {
                        value: this.#internalCustomer()?.firstName ?? '',
                        disabled: !this.editing(),
                    },
                ],
                middleName: [
                    {
                        value: this.#internalCustomer()?.middleName ?? '',
                        disabled: !this.editing(),
                    },
                ],
                lastName: [
                    {
                        value: this.#internalCustomer()?.lastName ?? '',
                        disabled: !this.editing(),
                    },
                ],
                email: [
                    {
                        value: this.#internalCustomer()?.email ?? '',
                        disabled: !this.editing(),
                    },
                    [SimpleEmailValidator()],
                    [this.#checkDuplicateEmailAsync()],
                ],
                phoneNumbers: this.#fb.array([]),
                customFields: this.#fb.array(
                    (this.customFieldDefinitions() ?? []).map(cfd => {
                        const existingCustomField =
                            this.#internalCustomer()?.customFields.find(
                                cf => cf.fieldDefinitionId === cfd.id,
                            );

                        const fieldGroup = this.#fb.group({
                            fieldDefinitionId: [
                                {
                                    value: cfd.id,
                                    disabled: !this.editing(),
                                },
                            ],
                            value: [
                                {
                                    value: existingCustomField?.value ?? '',
                                    disabled: !this.editing(),
                                },
                            ],
                        } as Record<
                            string,
                            {
                                value: string | number;
                                disabled: boolean;
                            }[]
                        >);

                        if (existingCustomField?.customFieldId !== undefined) {
                            fieldGroup.addControl(
                                'customFieldId',
                                this.#fb.control(
                                    existingCustomField.customFieldId,
                                ),
                            );
                        }

                        return fieldGroup;
                    }),
                ),
            },
            {
                validators: [this.#atLeastOneContactRequired()],
            },
        );

        const phoneNumbers = this.#internalCustomer()?.phoneNumbers ?? [];

        if (phoneNumbers.length > 0) {
            phoneNumbers.forEach(phoneNumber => {
                this.addPhoneNumber(
                    phoneNumber.phoneNumberId,
                    phoneNumber.number,
                    phoneNumber.type,
                    phoneNumber.preferred,
                );
            });
        } else {
            this.addPhoneNumber();
        }

        combineLatest([
            this.customerForm.valueChanges.pipe(
                tap(() => this.#checkIfFormChanged()),
            ),
            this.customerForm.statusChanges,
        ])
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(() => {
                this.#customerDetailsService.updateCanSave(
                    this.customerForm.valid && this.#checkIfFormChanged(),
                );
            });

        this.#initialFormValue = this.customerForm.getRawValue();
    }

    get phoneNumbers(): FormArray {
        return this.customerForm.get('phoneNumbers') as FormArray;
    }

    get customFields(): FormArray {
        return this.customerForm.get('customFields') as FormArray;
    }

    #checkIfFormChanged(): boolean {
        const currentFormValue = this.customerForm.getRawValue();
        return (
            JSON.stringify(this.#initialFormValue) !==
            JSON.stringify(currentFormValue)
        );
    }

    #getCustomField(
        fieldDefinitionId: number,
    ): ICustomFieldDefinition | undefined {
        return (this.customFieldDefinitions() ?? []).find(
            def => def.id === fieldDefinitionId,
        );
    }

    getCustomFieldLabel(fieldDefinitionId: number): string {
        const definition = this.#getCustomField(fieldDefinitionId);
        return definition ? definition.label : 'Unknown Field';
    }

    getCustomFieldType(
        fieldDefinitionId: number,
    ): CustomFieldDefinitionType | 'Unknown' {
        const definition = this.#getCustomField(fieldDefinitionId);
        return definition ? definition.type : 'Unknown';
    }

    getCustomFieldOptions(fieldDefinitionId: number): IDropdown<string>[] {
        const definition = this.#getCustomField(fieldDefinitionId);
        const options = definition ? (definition.options ?? []) : [];

        return [...options.map(o => ({ label: o, value: o, disabled: false }))];
    }

    getSelectedOption(fieldDefinitionId: number): string {
        const customField = this.customFields.controls.find(
            cf => cf.get('fieldDefinitionId')?.value === fieldDefinitionId,
        );

        return customField ? customField.get('value')?.value : '';
    }

    onOptionSelected(fieldDefinitionId: number, selectedValue: string) {
        const customField = this.customFields.controls.find(
            cf => cf.get('fieldDefinitionId')?.value === fieldDefinitionId,
        );

        if (customField) {
            customField.get('value')?.setValue(selectedValue);
            customField.get('value')?.markAsDirty();
            customField.get('value')?.markAsTouched();
            this.customerForm.updateValueAndValidity();
        }
    }

    addPhoneNumber(
        id = 0,
        number = '',
        type: string | null = null,
        preferred = false,
    ) {
        this.phoneNumbers.push(
            this.#fb.group(
                {
                    id: [id],
                    number: [
                        { value: number, disabled: !this.editing() },
                        [UKNumberValidator()],
                    ],
                    type: [type],
                    preferred: [preferred],
                },
                {
                    asyncValidators: [this.#checkDuplicatePhoneNumbersAsync()],
                },
            ),
        );
    }

    removePhoneNumber(index: number) {
        this.phoneNumbers.removeAt(index);
    }

    submitForm() {
        if (this.customerForm.valid) {
            if (this.#internalCustomer()) {
                this.#edit();
            } else {
                this.#create();
            }
        }
    }

    #atLeastOneContactRequired(): ValidatorFn {
        return (form: AbstractControl): ValidationErrors | null => {
            const email = form.get('email')?.value?.trim();
            const phoneNumbers =
                (form.get('phoneNumbers') as FormArray)?.controls || [];

            const hasPhoneNumber = phoneNumbers.some(phone =>
                phone.get('number')?.value?.trim(),
            );

            if (!email && !hasPhoneNumber) {
                return { atLeastOneContactRequired: true };
            }

            return null;
        };
    }

    #create() {
        const customer: ICustomerDetails = {
            customerId: '',
            externalProfiles: [],
            externalId: null,
            lastInteractionAt: null,
            createdAt: null,
            businessUnitId:
                this.#internalCustomer()?.businessUnitId ??
                this.#endCustomerStateService.bussinesUnit(),
            title: null,
            name: null,
            locale: null,
            firstName: this.customerForm.get('firstName')?.value,
            middleName: this.customerForm.get('middleName')?.value,
            lastName: this.customerForm.get('lastName')?.value,
            email: this.customerForm.get('email')?.value,
            phoneNumbers: this.phoneNumbers.controls
                .filter(phone => phone.get('number')?.value.trim())
                .map(phone => ({
                    phoneNumberId: 0,
                    number: phone.get('number')?.value,
                    type: phone.get('type')?.value,
                    preferred: phone.get('preferred')?.value,
                })),
            customFields: this.customFields.controls
                .filter(customField => customField.get('value')?.value.trim())
                .map(customField => ({
                    fieldDefinitionId:
                        customField.get('fieldDefinitionId')?.value,
                    customFieldId: customField.get('customFieldId')?.value,
                    value: customField.get('value')?.value,
                })),
            preferredAgentId: null,
        };

        this.#endCustomerApiService
            .createCustomer(customer)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
                next: customer => {
                    if (this.#endCustomerStateService.conversation()) {
                        this.#endCustomerApiService
                            .makeCustomerActive(
                                customer,
                                this.#endCustomerStateService.conversation() as string,
                            )
                            .subscribe();
                    }

                    this.#updateCustomerOnSave(customer);
                },
                error: customer =>
                    this.#logger.error('Failed to save customer', customer),
            });
    }

    #edit() {
        const patchData: JsonPatchData = [];

        updatableCustomerProperties.forEach(key => {
            const control = this.customerForm.get(key);

            if (control?.dirty) {
                let value = control.value;

                if (key === 'phoneNumbers' && Array.isArray(value)) {
                    value = value.filter(phone => phone.number?.trim());
                }

                patchData.push({
                    op: 'replace',
                    path: `/${key}`,
                    value: value,
                });
            } else if (
                key === 'phoneNumbers' &&
                Array.isArray(control?.value)
            ) {
                if (
                    control.value.length !==
                    this.#internalCustomer()?.phoneNumbers.length
                ) {
                    patchData.push({
                        op: 'replace',
                        path: `/${key}`,
                        value: control.value.filter(phone =>
                            phone.number?.trim(),
                        ),
                    });
                }
            }
        });

        this.#endCustomerApiService
            .updateCustomer(
                this.#internalCustomer() as ICustomerDetails,
                patchData,
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
                next: customer => {
                    if (this.#endCustomerStateService.conversation()) {
                        this.#endCustomerApiService
                            .makeCustomerActive(
                                customer,
                                this.#endCustomerStateService.conversation() as string,
                            )
                            .subscribe();
                    }

                    this.#updateCustomerOnSave(customer);
                },
                error: () =>
                    this.#logger.error('Failed to update customer', patchData),
            });
    }

    #checkDuplicateEmailAsync(): AsyncValidatorFn {
        return (
            control: AbstractControl,
        ): Observable<ValidationErrors | null> => {
            return control.value.trim().length > 0
                ? of(control.value).pipe(
                      switchMap(equalsTerm => {
                          return equalsTerm.length > 3 &&
                              this.#emailRegex.test(equalsTerm) &&
                              this.isCreateCustomer()
                              ? this.#customerDirectoryService
                                    .fetchCustomerDirectory(
                                        1,
                                        0,
                                        undefined,
                                        equalsTerm,
                                        this.#internalCustomer()
                                            ?.businessUnitId ??
                                            this.#endCustomerStateService.bussinesUnit(),
                                        false,
                                        true,
                                        false,
                                    )
                                    .pipe(
                                        map(response =>
                                            response.customerDirectory.length >
                                            0
                                                ? { emailExists: true }
                                                : null,
                                        ),
                                        catchError(() => of(null)),
                                    )
                              : of(null);
                      }),
                  )
                : of(null);
        };
    }

    #checkDuplicatePhoneNumbersAsync(): AsyncValidatorFn {
        return (form: AbstractControl): Observable<ValidationErrors | null> => {
            return of(form).pipe(
                switchMap(form => {
                    const equalsTerm = form.get('number')?.value?.trim();
                    return equalsTerm.length > 2 && this.isCreateCustomer()
                        ? this.#customerDirectoryService
                              .fetchCustomerDirectory(
                                  1,
                                  0,
                                  undefined,
                                  equalsTerm,
                                  this.#internalCustomer()?.businessUnitId ??
                                      this.#endCustomerStateService.bussinesUnit(),
                                  false,
                                  false,
                                  true,
                              )
                              .pipe(
                                  map(response =>
                                      response.customerDirectory.length > 0
                                          ? { phoneNumberExists: true }
                                          : null,
                                  ),
                                  catchError(() => of(null)),
                              )
                        : of(null);
                }),
            );
        };
    }

    #updateCustomerOnSave(customer: ICustomerDetails) {
        if (this.#endCustomerStateService.isActiveCustomerSelected()) {
            this.#endCustomerStateService
                .setActiveEndCustomer(customer.customerId)
                .subscribe();
        }

        this.#endCustomerStateService.setSelectedEndCustomer(customer);
        this.#customerDetailsService.updateEditing(false);
        this.updated.emit(customer);
    }
}
